import React, { createContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { authApi } from '@services/api/auth.api';
import { quotaApi } from '@services/api/quota.api';
import { storage } from '@services/storage/localStorage';
import { useQuotaSync } from '../hooks/useQuotaSync';
import type { AuthContextType } from '../types/auth.types';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [accessCode, setAccessCode] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [questionsRemaining, setQuestionsRemaining] = useState<number>(0);

  // Handle real-time quota updates from SSE
  const handleQuotaUpdate = useCallback((data: { questionsRemaining: number }) => {
    setQuestionsRemaining(data.questionsRemaining);
  }, []);

  // Set up real-time quota synchronization
  useQuotaSync({
    onQuotaUpdate: handleQuotaUpdate,
    enabled: isAuthenticated,
  });

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedCode = storage.getAccessCode();
    if (storedCode) {
      setAccessCode(storedCode);
      setIsAuthenticated(true);
      checkQuota();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const validateAccessCode = async (code: string): Promise<boolean> => {
    try {
      const response = await authApi.validateAccessCode(code);

      if (response.valid) {
        storage.setAccessCode(code);
        setAccessCode(code);
        setIsAuthenticated(true);
        await checkQuota();
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Access code validation failed:', error);
      return false;
    }
  };

  const logout = () => {
    storage.removeAccessCode();
    setAccessCode(null);
    setIsAuthenticated(false);
    setQuestionsRemaining(0);
  };

  const checkQuota = async (): Promise<void> => {
    try {
      const response = await quotaApi.checkQuota();
      setQuestionsRemaining(response.questionsRemaining);
    } catch (error) {
      console.error('Failed to check quota:', error);
      // If quota check fails due to auth, logout
      if (typeof error === 'object' && error !== null && 'status' in error && (error as { status: number }).status === 401) {
        logout();
      }
    }
  };

  const value: AuthContextType = {
    accessCode,
    isAuthenticated,
    questionsRemaining,
    validateAccessCode,
    logout,
    checkQuota,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
